import { AuthApi } from '../auth/authApi';
import type {
  DeleteFileFromMissingItemRequestDTO,
  DownloadFileFromMissingItemRequestDTO,
  MissingItemFormDetailDTO,
  UpdateMissingItemFormFieldRequestDTO,
  UpdateMissingItemFormFieldResponseDTO,
  UploadFileToMissingItemRequestDTO,
  UserMissingItemFormsDTO,
} from './DTOs';
import { Singleton } from '@/decorators';
import { generateQueryString } from '@/libs';
import type { Result } from '@/types/result.interface';
import type { AppError } from '@/types/errors.interface';
import { wrapHttpCall } from '../utils/apiResultMapper';
import { SAVE_REQUEST_TIMEOUT_MS } from '@/api/base/saveRequestTimeout';
import type { UpdateSignatureResponseDTO } from '@/api/signature/DTOs';
import type { OperationResultResponseDTO } from '@/api/base/DTOs';
import type {
  ClearSectionResponseDTO,
  SectionGroupResponseDTO,
  UpdateSectionConditionalFieldsGroupRequestDTO,
  UpdateSectionConditionalFieldsGroupResponseDTO,
  UpdateStagedFieldsGroupRequestDTO,
  UpdateStagedFieldsGroupResponseDTO,
} from '@/api/section/DTOs';

/**
 * Missing Item Form API (Framework Agnostic)
 *
 * Handles missing item form list, detail retrieval, and field updates.
 */
@Singleton
export class MissingItemApi extends AuthApi {
  constructor() {
    super();
  }

  async getUserMissingItemForms(): Promise<Result<UserMissingItemFormsDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.get<UserMissingItemFormsDTO>('/MissingItemForm')
    );
  }

  async getMissingItemForm(id: number): Promise<Result<MissingItemFormDetailDTO | null, AppError>> {
    return wrapHttpCall(() =>
      this.api.get<MissingItemFormDetailDTO | null>(`/MissingItemForm/${id}`)
    );
  }

  async updateMissingItemFormField(
    missingItemFormId: number,
    body: UpdateMissingItemFormFieldRequestDTO,
  ): Promise<Result<UpdateMissingItemFormFieldResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<UpdateMissingItemFormFieldResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/field`,
        body,
        { timeout: SAVE_REQUEST_TIMEOUT_MS },
      ),
    );
  }

  async addMissingItemFormGroup(
    missingItemFormId: number,
    groupId: string,
  ): Promise<Result<SectionGroupResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<SectionGroupResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/group/${groupId}`,
      ),
    );
  }

  async removeMissingItemFormGroup(
    missingItemFormId: number,
    groupId: string,
  ): Promise<Result<SectionGroupResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.delete<SectionGroupResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/group/${groupId}`,
      ),
    );
  }

  async clearMissingItemFormGroup(
    missingItemFormId: number,
    groupId: string,
  ): Promise<Result<ClearSectionResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<ClearSectionResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/group/${groupId}/clear`,
      ),
    );
  }

  async updateMissingItemFormStagedFieldsGroup(
    missingItemFormId: number,
    formField: UpdateStagedFieldsGroupRequestDTO['formField'],
  ): Promise<Result<UpdateStagedFieldsGroupResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<UpdateStagedFieldsGroupResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/stagedGroup/${formField.groupID}`,
        formField,
        { timeout: SAVE_REQUEST_TIMEOUT_MS },
      ),
    );
  }

  async updateMissingItemFormConditionalFieldsGroup(
    missingItemFormId: number,
    formField: UpdateSectionConditionalFieldsGroupRequestDTO['formField'],
  ): Promise<Result<UpdateSectionConditionalFieldsGroupResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<UpdateSectionConditionalFieldsGroupResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/conditionalGroup/${formField.groupID}`,
        formField,
        { timeout: SAVE_REQUEST_TIMEOUT_MS },
      ),
    );
  }

  async getMissingItemSignature(): Promise<Result<string, AppError>> {
    return wrapHttpCall(() =>
      this.api.get<string>('/MissingItemForm/signature'),
    );
  }

  async updateMissingItemSignature(
    signature: string,
  ): Promise<Result<UpdateSignatureResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<UpdateSignatureResponseDTO>(
        '/MissingItemForm/signature',
        signature,
      ),
    );
  }

  async getMissingItemSignatureName(): Promise<Result<string | null, AppError>> {
    return wrapHttpCall(() =>
      this.api.get<string | null>('/MissingItemForm/signatureName'),
    );
  }

  async updateMissingItemSignatureName(
    signatureName: string,
  ): Promise<Result<UpdateSignatureResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<UpdateSignatureResponseDTO>(
        '/MissingItemForm/signatureName',
        signatureName,
      ),
    );
  }

  async confirmMissingItemForm(
    missingItemFormId: number,
  ): Promise<Result<OperationResultResponseDTO, AppError>> {
    return wrapHttpCall(() =>
      this.api.post<OperationResultResponseDTO>(
        `/MissingItemForm/${missingItemFormId}/confirm`,
        {},
      ),
    );
  }

  async uploadFile(
    body: UploadFileToMissingItemRequestDTO,
  ): Promise<Result<string, AppError>> {
    const formData = new FormData();
    formData.append('File', body.file);

    return wrapHttpCall(() =>
      this.api.post<string>(
        `/MissingItemForm/${body.missingItemFormId}/uploadFile/${body.itemId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: body.onUploadProgress,
          signal: body.cancelToken,
        },
      ),
    );
  }

  async downloadFile(
    body: DownloadFileFromMissingItemRequestDTO,
  ): Promise<Result<unknown, AppError>> {
    const queryString = generateQueryString({ RealName: body.realName });

    return wrapHttpCall(() =>
      this.api.get(
        `/MissingItemForm/${body.missingItemFormId}/downloadFile/${body.itemId}${queryString}`,
      ),
    );
  }

  async deleteFile(
    body: DeleteFileFromMissingItemRequestDTO,
  ): Promise<Result<boolean, AppError>> {
    const queryString = generateQueryString({ RealName: body.realName });

    return wrapHttpCall(() =>
      this.api.post<boolean>(
        `/MissingItemForm/${body.missingItemFormId}/deleteFile/${body.itemId}${queryString}`,
      ),
    );
  }
}
